import { DURATION_OPTIONS, PLATFORMS } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { PLATFORM_CONFIG } from "../../utils/platformConfigFields";
import TaskEditForm from "./TaskEditForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  PlatformDropdown,
  AssigneeDropdown,
  MultiSelectAssigneeDropdown,
  StatusBadge,
  CustomDateInput,
  parseTimeString,
  formatToHHMM,
  CustomTimeInput,
} from "./Task.utils";

import { useState } from "react";
import { createPortal } from "react-dom";
import MultiSelect from "../../components/common/MultiSelect";
import { useSelector } from "react-redux";

// Task Options Dropdown For Select Label Tag etc
const TaskOptionsDropdown = ({
  options = [],
  value = "",
  onChange,
  disabled,
  type,
}) => {
  if (!options.length) return <span className="text-muted">—</span>;

  if (type === "multiselect") {
    const formattedOptions = options.map((opt) => ({
      value: opt.key,
      label: opt.value,
    }));

    return (
      <MultiSelect
        options={formattedOptions}
        value={value || []}
        onChange={onChange}
        placeholder="Select..."
        avatarMode={true}
        disabled={disabled}
      />
    );
  }

  return (
    <select
      className="form-select form-select-sm"
      style={{
        minWidth: "90px",
        height: "38px",
        fontSize: "12px",
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">— Select —</option>

      {options.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.value}
        </option>
      ))}
    </select>
  );
};

const TaskCard = ({
  action,
  selectedTasks,
  editingTaskId,
  editedTaskName,
  editedTaskDescription,
  settings = [],
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteTask,
  onSend,
  onKeyDown,
  onTaskNameChange,
  onDescriptionChange,
  onToggleTask,
  participants,
  executingTaskIds = new Set(),
  onTaskUpdate,
  serialNo,
  columns = [],
  platformType,
}) => {
  const isEditing = editingTaskId === action.uuid;
  const isSelected = selectedTasks.includes(action.uuid);
  const isLoading = executingTaskIds.has(action.uuid);
  const [errorModal, setErrorModal] = useState(null);

  const key = `${platformType}_${action.settingId}`;

  const taskOptionsFromRedux = useSelector(
    (state) => state.task.taskOptions[key],
  );

  const taskOptions = taskOptionsFromRedux || {};

  const config = PLATFORM_CONFIG[platformType] || {};
  const colSpan = columns.length;
  const handleExecute = () => onSend([action.uuid]);

  // Inline Editing For Task
  if (isEditing) {
    return (
      <tr className="editRow">
        <td colSpan={colSpan}>
          <TaskEditForm
            action={action}
            editedTaskName={editedTaskName}
            editedTaskDescription={editedTaskDescription}
            onTaskNameChange={onTaskNameChange}
            onDescriptionChange={onDescriptionChange}
            onSave={() => onSaveEdit(action)}
            onCancel={onCancelEdit}
            onKeyDown={onKeyDown}
            onDeleteTask={() => onDeleteTask(action)}
          />
        </td>
      </tr>
    );
  }

  const showExecuteBtn = !isEditing && !isSelected;

  // render or show row of task with different columns
  const renderCell = (col) => {
    switch (col.key) {
      case "checkbox":
        return isLoading ? (
          <span
            className="spinner-border spinner-border-sm text-primary"
            style={{ width: "1rem", height: "1rem" }}
          />
        ) : (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleTask(action.uuid)}
            className="form-check-input"
          />
        );
      case "task":
        return (
          <div
            className="d-flex align-items-center gap-1 w-100 cursor-pointer taskCellWrap"
            onClick={() => !isLoading && onStartEdit(action)}
          >
            <span
              className="text-body-tertiary flex-shrink-0"
              style={{ fontSize: "12px" }}
            >
              {serialNo}.
            </span>
            <span
              className="taskName"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={action.name}
            >
              {action.name}
            </span>
          </div>
        );

      case "platform":
        return (
          <PlatformDropdown
            settings={settings}
            action={action}
            disabled={isLoading}
            onSettingChange={(settingId) =>
              onTaskUpdate(action.uuid, { settingId })
            }
          />
        );

      case "assignee":
        if (config.assigneeType === "none")
          return <span className="text-muted">Test</span>;
        if (config.assigneeType === "single") {
          return (
            <AssigneeDropdown
              participants={participants}
              action={action}
              onAssigneeChange={(email) =>
                onTaskUpdate(action.uuid, { email: [email] })
              }
            />
          );
        }
        return (
          <MultiSelectAssigneeDropdown
            participants={participants}
            action={action}
            onAssigneeChange={(values) =>
              onTaskUpdate(action.uuid, {
                email: Array.isArray(values) ? values : [values],
              })
            }
          />
        );

      case "date":
      case "due_on":
        return (
          <DatePicker
            selected={action[col.key] ? new Date(action[col.key]) : null}
            onChange={(date) =>
              onTaskUpdate(action.uuid, { [col.key]: date?.toISOString() })
            }
            minDate={new Date()}
            dateFormat="MMM dd, yyyy"
            customInput={<CustomDateInput placeholder="Select Date" />}
          />
        );

      case "start_time":
        return (
          <DatePicker
            selected={parseTimeString(action.start_time)}
            onChange={(date) =>
              onTaskUpdate(action.uuid, { startTime: formatToHHMM(date) })
            }
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            dateFormat="hh:mm aa"
            customInput={<CustomTimeInput placeholder="Time" />}
          />
        );

      case "duration":
        return (
          <select
            className="form-select form-select-sm w-auto"
            style={{
              minWidth: "90px",
              height: "32px",
              fontSize: "12px",
            }}
            value={action.duration || ""}
            onChange={(e) =>
              onTaskUpdate(action.uuid, { duration: e.target.value })
            }
          >
            <option value="">—</option>
            {DURATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "priority": {
        if (platformType === PLATFORMS.TODOIST) {
          return (
            <select
              className="form-select form-select-sm"
              value={action.priority || ""}
              onChange={(e) =>
                onTaskUpdate(action.uuid, {
                  priority: e.target.value,
                })
              }
            >
              <option value="">—</option>
              <option value="1">P1</option>
              <option value="2">P2</option>
              <option value="3">P3</option>
              <option value="4">P4</option>
            </select>
          );
        }

        const options = taskOptions[col.key] || [];

        return (
          <TaskOptionsDropdown
            options={options}
            value={action[col.key] || ""}
            disabled={isLoading}
            onChange={(selectedKey) =>
              onTaskUpdate(action.uuid, {
                [col.key]: selectedKey,
              })
            }
            type={col.type}
          />
        );
      }

      case "labels":
      case "tags":
      case "list":
      case "properties":
      case "milestone":
      case "sprint": {
        const options = taskOptions[col.key] || [];

        return (
          <TaskOptionsDropdown
            options={options}
            value={action[col.key] || ""}
            disabled={isLoading}
            onChange={(selectedKey) =>
              onTaskUpdate(action.uuid, {
                [col.key]: selectedKey,
              })
            }
            type={col.type}
          />
        );
      }

      case "permission":
        return (
          <select
            className="form-select form-select-sm"
            style={{
              minWidth: "90px",
              height: "38px",
              fontSize: "12px",
            }}
            value={action.permission || ""}
            onChange={(e) =>
              onTaskUpdate(action.uuid, { permission: e.target.value })
            }
          >
            <option value="">—</option>
            <option value="send">Send Mail</option>
            <option value="draft">Save Draft</option>
          </select>
        );

      case "status":
        return <StatusBadge status={action.status} />;

      case "result":
        return action?.actionLogId?.status === "success" &&
          action?.actionLogId?.result ? (
          <a
            href={action.actionLogId.result}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
            style={{ fontSize: "12px" }}
          >
            View <i className={`wb ${Icons("redirect")}`} />
          </a>
        ) : (
          <span className="text-muted">—</span>
        );

      case "failure":
        return action?.actionLogId?.status === "failure" &&
          action?.actionLogId?.result ? (
          <span
            className="badge bg-danger-subtle text-danger"
            style={{ cursor: "pointer" }}
            onClick={() => setErrorModal(action.actionLogId.result)}
          >
            !
          </span>
        ) : (
          <span className="text-muted">—</span>
        );

      case "actions":
        return (
          <div className="d-flex gap-1 justify-content-end">
            {showExecuteBtn && (
              <button
                className="btn btn-sm btn-primary"
                onClick={handleExecute}
                disabled={!action.settingId || isLoading}
              >
                <i className={`wb ${Icons("resume")}`} />
              </button>
            )}
            <button
              className="btn btn-sm btn-phoenix-danger"
              onClick={() => onDeleteTask(action)}
              disabled={isLoading}
            >
              <i className={`wb ${Icons("remove")}`} />
            </button>
          </div>
        );

      default:
        return <span className="text-muted">—</span>;
    }
  };

  return (
    <>
      <tr
        style={{
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        {columns.map((col) => (
          <td
            key={col.key}
            className="align-middle"
            style={{
              width: col.width,
              padding: "6px 8px",
              verticalAlign: "middle",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: col.key === "task" ? "nowrap" : "nowrap",
            }}
          >
            {renderCell(col)}
          </td>
        ))}
      </tr>

      {errorModal &&
        createPortal(
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
            onClick={() => setErrorModal(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <div>
                    {" "}
                    <h6 className="modal-title text-danger">Failure Details</h6>
                  </div>
                  <div>
                    <button
                      className="btn-close"
                      onClick={() => setErrorModal(null)}
                    />
                  </div>
                </div>
                <div className="modal-body">
                  <pre style={{ whiteSpace: "pre-wrap", fontSize: "12px" }}>
                    {errorModal}
                  </pre>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TaskCard;
